const SDK_GROUP = ["SDK_feature", "sdk_feature"];
const FEATURE_ITEMS = {
    appointment: ["appointment.enabled"],
    appointmentSessionPacks: ["appointment.enablesessionpacks"],
    calendarOnly: ["appointment.enablecalendaronlymode"],
    doctorSearchFilters: ["appointment.enabledoctorsearchandfilters"],
    onlinePayment: ["appointment.enableonlinepayment", "packages.enableonlinepayment"],
    patientForm: ["appointment.enabledynamicformstep"],
    payAtClinic: ["appointment.enablepayatclinic", "packages.enablepayatclinic"],
    packagePurchase: ["packages.enabled"],
    packageUsage: ["packages.enablesessionpackusage"],
    qms: ["qms.enabled", "qms.enablewalkinqueuebooking"],
    qmsPatientLookup: ["qms.enablepatientlookup"],
    qmsSlotSelection: ["qms.enableslotselection"],
    videoConsultation: [],
};
const SUPPORTING_GROUPS = {
    appointmentSessionPacks: ["Session_packs", "session_packs"],
    packagePurchase: ["Session_packs", "session_packs"],
    packageUsage: ["Session_packs", "session_packs"],
    patientForm: ["Patient_form", "patient_form"],
    qms: ["Qms_appointment", "qms_appointment"],
    qmsPatientLookup: ["Qms_appointment", "qms_appointment"],
    qmsSlotSelection: ["Qms_appointment", "qms_appointment"],
    videoConsultation: ["Video_consultation", "video_consultation"],
};
export function resolveWorkspaceFeatureAccess(workspace) {
    return {
        appointment: isFeatureEnabled(workspace, "appointment"),
        appointmentSessionPacks: workspace?.arePackagesConfigured !== false &&
            isFeatureEnabled(workspace, "appointmentSessionPacks") &&
            isFeatureEnabled(workspace, "packagePurchase"),
        calendarOnly: isFeatureEnabled(workspace, "calendarOnly"),
        doctorSearchFilters: isFeatureEnabled(workspace, "doctorSearchFilters"),
        onlinePayment: isFeatureEnabled(workspace, "onlinePayment"),
        patientForm: isFeatureEnabled(workspace, "patientForm"),
        payAtClinic: isFeatureEnabled(workspace, "payAtClinic"),
        packagePurchase: workspace?.arePackagesConfigured !== false &&
            isFeatureEnabled(workspace, "packagePurchase"),
        packageUsage: workspace?.arePackagesConfigured !== false &&
            isFeatureEnabled(workspace, "packageUsage"),
        qms: isFeatureEnabled(workspace, "qms"),
        qmsPatientLookup: isFeatureEnabled(workspace, "qmsPatientLookup"),
        qmsSlotSelection: isFeatureEnabled(workspace, "qmsSlotSelection"),
        videoConsultation: isFeatureEnabled(workspace, "videoConsultation"),
        invoice: workspace?.isAppointmentInvoiceConfig !== false,
    };
}
export function isAppointmentSystemTypeEnabled(workspace, systemType) {
    const features = resolveWorkspaceFeatureAccess(workspace);
    if (systemType === "QMS")
        return features.qms;
    return features.appointment;
}
function isFeatureEnabled(workspace, feature) {
    if (!workspace || !hasCatalog(workspace)) {
        return true;
    }
    if (isAnySupportingGroupExceeded(workspace, feature)) {
        return false;
    }
    const supportingGroups = SUPPORTING_GROUPS[feature] ?? [];
    if (supportingGroups.length > 0 && !hasAllGroups(workspace, supportingGroups)) {
        return false;
    }
    const itemKeys = FEATURE_ITEMS[feature];
    if (itemKeys.length === 0)
        return true;
    return itemKeys.some((itemKey) => hasAccessibleItem(workspace, SDK_GROUP, itemKey));
}
function hasCatalog(workspace) {
    return (Object.keys(workspace.features ?? {}).length > 0 ||
        Object.keys(workspace.usageLimits ?? {}).length > 0);
}
function hasAllGroups(workspace, aliases) {
    const byCanonical = new Set(aliases.map(canonicalize));
    for (const alias of byCanonical) {
        const group = findGroup(workspace, [alias]);
        if (!group || isUsageExceeded(workspace, [alias])) {
            return false;
        }
    }
    return true;
}
function isAnySupportingGroupExceeded(workspace, feature) {
    return (SUPPORTING_GROUPS[feature] ?? []).some((group) => isUsageExceeded(workspace, [group]));
}
function hasAccessibleItem(workspace, groupAliases, itemKey) {
    const group = findGroup(workspace, groupAliases);
    if (!group || isUsageExceeded(workspace, groupAliases)) {
        return false;
    }
    const items = group.items ?? {};
    const item = Object.entries(items).find(([key]) => canonicalize(key) === canonicalize(itemKey))?.[1];
    if (!item)
        return false;
    const access = Object.values(item.access ?? {});
    return access.length === 0 || access.some((value) => value !== "no_access");
}
function findGroup(workspace, aliases) {
    const wanted = new Set(aliases.map(canonicalize));
    return Object.entries(workspace.features ?? {}).find(([key]) => wanted.has(canonicalize(key)))?.[1];
}
function isUsageExceeded(workspace, aliases) {
    const wanted = new Set(aliases.map(canonicalize));
    const usage = Object.entries(workspace.usageLimits ?? {}).find(([key]) => wanted.has(canonicalize(key)))?.[1];
    if (!usage)
        return false;
    const limit = String(usage.limitValue ?? "").toLowerCase();
    if (!limit || limit === "unlimited")
        return false;
    const remaining = toNumber(usage.remainingValue);
    if (remaining !== null)
        return remaining <= 0;
    const current = toNumber(usage.currentUsage);
    const max = toNumber(usage.limitValue);
    return current !== null && max !== null && current >= max;
}
function toNumber(value) {
    if (value === null || value === undefined || value === "")
        return null;
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
}
function canonicalize(value) {
    return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}
